import React from 'react';
import PropTypes from 'prop-types';
import {Tooltip, OverlayTrigger} from 'react-bootstrap';
import Editor from 'draft-js-plugins-editor';
import {
  ItalicButton,
  BoldButton,
  CodeButton,
  UnorderedListButton,
  OrderedListButton,
  BlockquoteButton,
  CodeBlockButton,
} from '@draft-js-plugins/buttons';
import {RichUtils} from 'draft-js';
import createToolbarPlugin from 'draft-js-static-toolbar-plugin';
import createMarkdownPlugin from 'draft-js-markdown-plugin-es6';
import 'draft-js/dist/Draft.css';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {object} from 'check-types';

import Button from './ButtonNew';

const languages = {
  bash: 'Bash',
  c: 'C',
  cpp: 'C++',
  css: 'CSS',
  go: 'Go',
  html: 'HTML',
  java: 'Java',
  js: 'JavaScript',
  kotlin: 'Kotlin',
  perl: 'Perl',
  php: 'PHP',
  python: 'Python',
  ruby: 'Ruby',
  rust: 'Rust',
  scala: 'Scala',
  sql: 'SQL',
  swift: 'Swift',
};

const staticToolbarPlugin = createToolbarPlugin();
const {Toolbar} = staticToolbarPlugin;

// TODO: Undo a lot of the styling coming from code.less, which I think is coming from bootstrap
const DraftEditor = ({
  editorState,
  setEditorState,
  editorRef,
  showToolbar,
  placeholderText,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const plugins = [createMarkdownPlugin({languages}), staticToolbarPlugin];

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleOnTab = (e) => {
    e.preventDefault();
    setEditorState(RichUtils.onTab(e, editorState, 4));
  };

  return (
    <>
      <div className="p-5 text-base">
        <Editor
          editorState={editorState}
          onChange={setEditorState}
          placeholder={placeholderText}
          plugins={plugins}
          ref={editorRef}
          handleKeyCommand={handleKeyCommand}
          onTab={(e) => handleOnTab(e)}
          spellCheck
          autoFocus
        />
      </div>
      <div className="flex items-center justify-between p-2">
        <div className="flex items-center">
          {showToolbar && (
            <Toolbar>
              {(externalProps) => (
                <div className="CommentEditor__toolbar">
                  <BoldButton {...externalProps} />
                  <ItalicButton {...externalProps} />
                  <CodeButton {...externalProps} />
                  <UnorderedListButton {...externalProps} />
                  <OrderedListButton {...externalProps} />
                  <BlockquoteButton {...externalProps} />
                  <CodeBlockButton {...externalProps} />
                  <OverlayTrigger
                    overlay={<Tooltip id="comments-tooltip">Markdown formatting and shortcuts supported</Tooltip>}
                  >
                    <div className="ml-2">
                      <FontAwesomeIcon className="icon-edit" icon={faInfoCircle} />
                    </div>
                  </OverlayTrigger>
                </div>
              )}
            </Toolbar>
          )}
        </div>
        <div className="flex items-center">
          {onCancel && (
            <button className="p-2 cursor-pointer mr-4 font-medium" type="button" onClick={onCancel}>Cancel</button>
          )}
          <span>
            <Button disabled={submitting} onClick={onSubmit}>
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </span>
        </div>
      </div>
    </>
  );
};

DraftEditor.propTypes = {
  editorState: PropTypes.shape().isRequired,
  setEditorState: PropTypes.func.isRequired,
  editorRef: PropTypes.instanceOf(object).isRequired,
  showToolbar: PropTypes.bool.isRequired,
  placeholderText: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DraftEditor;
